import { Factory, GridView, Search } from '@mui/icons-material';
import { Box, SelectChangeEvent } from '@mui/material';
import React, { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ProductSearchPanelProps as RoomComponentsSearchPanelProps } from '../../@types/ContextProviderRelated';
import FilesAPI2DModel from '../../models/standard/FilesAPI2DModel';
import FilesAPIProductModel from '../../models/standard/FilesAPIProductModel';
import { useRoomComponentsData } from '../../provider/RoomComponentsDataProvider';
import { PRODUCT_GALLERY_INPUT_SEARCH_DELAY } from '../../utils/constants';
import { searchInFetchedData, searchInFetchedModels2D } from '../../utils/searchUtils';
import ImsSelect from './ImsSelect';
import ImsTextField from './ImsTextField';

const RoomComponentsSearchPanel = ({ returnResults, products, showManufacturersFilter }: RoomComponentsSearchPanelProps): JSX.Element => {
  const intl = useIntl();
  const providerData = useRoomComponentsData();

  const [filteredManufacturers, setFilteredManufacturers] = useState<string[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<string[]>([]);
  const [searchString, setSearchString] = useState<string>('');
  const [searchResults, setSearchResults] = useState<(FilesAPIProductModel | FilesAPI2DModel)[]>([]);

  const searchTimerRef = useRef<number | null>(null);

  const allManufacturersLabel = intl.formatMessage({ id: 'ProductGallery.allManufacturers' });
  const allCategoriesLabel = intl.formatMessage({ id: 'ProductGallery.allCategories' });

  // Derive manufacturers from the products prop
  const manufacturers = useMemo(() => {
    if (showManufacturersFilter && Array.isArray(products)) {
      const names = Array.from(
        new Set(products.map((product: FilesAPIProductModel) => product.manufacturer?.name).filter((name: string | undefined): name is string => !!name)),
      );
      return names.length > 0 ? names : [allManufacturersLabel];
    }
    return [];
  }, [products, showManufacturersFilter, allManufacturersLabel]);

  // Derive categories based on tab
  const categories = useMemo(() => {
    if (!showManufacturersFilter && providerData.models2D) {
      // Models tab: derive categories from providerData.models2D
      const cats = new Set<string>();
      providerData.models2D.forEach((model: FilesAPI2DModel) => {
        if (model.object?.name) {
          cats.add(model.object.name);
        }
      });
      return cats.size > 0 ? Array.from(cats) : [allCategoriesLabel];
    } else if (showManufacturersFilter && Array.isArray(products)) {
      // Products tab: derive categories from products
      const cats = new Set<string>();
      products.forEach((product: FilesAPIProductModel) => {
        if (product.objects) {
          product.objects.forEach((obj: any) => {
            if (obj.name) {
              cats.add(obj.name);
            }
          });
        }
      });
      return cats.size > 0 ? Array.from(cats) : [allCategoriesLabel];
    }
    return [allCategoriesLabel];
  }, [showManufacturersFilter, providerData.models2D, products, allCategoriesLabel]);


  // Check if user is actively searching
  const isSearching = useMemo(() => {
    const hasManufacturerFilter =
      filteredManufacturers.length > 0 && !(filteredManufacturers.length === 1 && filteredManufacturers[0] === allManufacturersLabel);
    const hasCategoryFilter =
      filteredCategories.length > 0 && !(filteredCategories.length === 1 && filteredCategories[0] === allCategoriesLabel);
    return searchString.trim() !== '' || hasManufacturerFilter || hasCategoryFilter;
  }, [searchString, filteredManufacturers, filteredCategories, allManufacturersLabel, allCategoriesLabel]);

  const triggerSearch = () => {
    providerData.setIsUserSearching(isSearching);
    if (isSearching) {
      if (showManufacturersFilter) {
        // Products tab: use your existing product search logic.
        const results = searchInFetchedData(
          providerData,
          searchString,
          filteredManufacturers.length > 0 ? filteredManufacturers : [allManufacturersLabel],
          filteredCategories.length > 0 ? filteredCategories : [allCategoriesLabel],
          allManufacturersLabel,
          allCategoriesLabel,
        );
        setSearchResults(results);
      } else {
        // Models tab: use the new 2D models search, including category filtering.
        const results = searchInFetchedModels2D(
          providerData.models2D,
          searchString,
          filteredCategories.length > 0 ? filteredCategories : [allCategoriesLabel],
          allCategoriesLabel,
        );
        setSearchResults(results);
      }
    } else {
      setSearchResults([]);
    }
  };

  useEffect(() => {
    // Reset all search state when switching tabs.
    setSearchString('');
    setFilteredManufacturers([]);
    setFilteredCategories([]);
    setSearchResults([]);
    returnResults([]); // Propagate empty search results to the parent.
  }, [showManufacturersFilter]);

  // **Search Trigger with Debounce**
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }

    if (isSearching) {
      searchTimerRef.current = window.setTimeout(() => {
        triggerSearch();
      }, PRODUCT_GALLERY_INPUT_SEARCH_DELAY);
    } else {
      triggerSearch();
    }

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString, filteredManufacturers, filteredCategories]);

  // **Reset manufacturer filter when switching to "Models"**
  useEffect(() => {
    if (!showManufacturersFilter) {
      setFilteredManufacturers([]); // Reset manufacturers
    }
  }, [showManufacturersFilter]);

  // **Notify Parent Component Whenever Search Results Change**
  useEffect(() => {
    returnResults(searchResults);
  }, [searchResults, returnResults]);

  // **Handle Input Changes**
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchString(e.target.value);
  };

  // **Trigger Search on Enter Key Press**
  const handleKeyPress = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter') {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
        searchTimerRef.current = null;
      }
      triggerSearch();
    }
  };

  // **Handle Manufacturer and Category Changes**
  const handleManufacturerChange = (e: SelectChangeEvent<string[]>): void => {
    let newSelection = e.target.value;
    if (typeof newSelection === 'string') {
      newSelection = newSelection.split(',');
    }
    if (newSelection.includes(allManufacturersLabel)) {
      newSelection = [allManufacturersLabel];
    }
    setFilteredManufacturers(newSelection);
  };

  const handleCategoryChange = (e: SelectChangeEvent<string[]>): void => {
    let newSelection = e.target.value;
    if (typeof newSelection === 'string') {
      newSelection = newSelection.split(',');
    }
    if (newSelection.includes(allCategoriesLabel)) {
      newSelection = [allCategoriesLabel];
    }
    setFilteredCategories(newSelection);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '95%', mx: 'auto', mt: 1 }}>
      {/* Categories (Objects) */}
      <ImsSelect
        multiple
        options={categories}
        value={filteredCategories}
        onChange={handleCategoryChange}
        placeholderId="RoomComponentsSearchPanel.categories"
        icon={GridView}
      />
      {/* Manufacturers - Render only if showManufacturersFilter is true */}
      {showManufacturersFilter && (
        <ImsSelect
          multiple
          options={manufacturers}
          value={filteredManufacturers}
          onChange={handleManufacturerChange}
          placeholderId="RoomComponentsSearchPanel.filterManufacturers"
          icon={Factory}
        />
      )}
      {/* Search */}
      <ImsTextField
        value={searchString}
        onChange={handleInputChange}
        onKeyDown={handleKeyPress}
        placeholderId="RoomComponentsSearchPanel.search"
        icon={<Search />}
      />
    </Box>
  );
};

export default RoomComponentsSearchPanel;